'use client';
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { X, Clock, MapPin, Search, Map, List, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { SCHOOLS } from '@/data/schools';
import { ALL_COMMUNITIES_FOR_SEARCH, ALL_COMMUNITY_NAMES, estimatePeakTravelMinsFromCommunity, formatCommuteTime } from '@/data/communityPrices';
import { trackCommunitySearch } from '@/lib/analytics/ga4Events';

interface Props {
  onClose: () => void;
  onSelectCommunity: (community: string, maxMinutes: number) => void;
}

const COMMUTE_BANDS = [10, 15, 20, 25, 30] as const;

// ─── Shared calculation helpers ─────────────────────────────────────────────

function calcSchoolsNearCommunity(communityName: string, maxMinutes: number) {
  const communityData = ALL_COMMUNITIES_FOR_SEARCH.find(c => c.name === communityName);
  if (!communityData) return [];
  const allWithTimes = SCHOOLS.map(s => {
    const mins = estimatePeakTravelMinsFromCommunity(communityData, s.coordinates.lat, s.coordinates.lng);
    return { school: s, mins };
  }).sort((a, b) => a.mins - b.mins);
  // Only return schools genuinely within the threshold — raw value comparison, no rounding
  return allWithTimes
    .filter(({ mins }) => mins <= maxMinutes)
    .map(({ school, mins }) => ({ ...school, travelMins: mins }));
}

function calcCommunitiesNearSchool(schoolId: string, maxMinutes: number) {
  const school = SCHOOLS.find(s => s.id === schoolId);
  if (!school) return [];
  return ALL_COMMUNITIES_FOR_SEARCH.map(c => {
    const mins = estimatePeakTravelMinsFromCommunity(c, school.coordinates.lat, school.coordinates.lng);
    return { name: c.name, mins, isSubCommunity: c.isSubCommunity, parentCommunity: c.parentCommunity };
  }).filter(c => c.mins <= maxMinutes).sort((a, b) => a.mins - b.mins);
}

// ─── Mobile Bottom Sheet ─────────────────────────────────────────────────────

function MobileCommuteSheet({ onClose, onSelectCommunity }: Props) {
  const [mode, setMode] = useState<'community-to-school' | 'school-to-community'>('community-to-school');
  const [selectedCommunity, setSelectedCommunity] = useState('');
  const [selectedSchool, setSelectedSchool] = useState('');
  const [maxMinutes, setMaxMinutes] = useState<number>(20);
  const [communitySearch, setCommunitySearch] = useState('');
  const [schoolSearch, setSchoolSearch] = useState('');
  const [showCommunityPicker, setShowCommunityPicker] = useState(false);
  const [showSchoolPicker, setShowSchoolPicker] = useState(false);
  const [resultsView, setResultsView] = useState<'list' | 'map'>('list');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Track community_search when school-to-community mode produces results
  const lastCommunitySearchRef = useRef<string>('');

  const filteredCommunities = useMemo(() =>
    ALL_COMMUNITY_NAMES.filter(c => c.toLowerCase().includes(communitySearch.toLowerCase())).slice(0, 80),
    [communitySearch]
  );

  const filteredSchools = useMemo(() =>
    SCHOOLS.filter(s => s.name.toLowerCase().includes(schoolSearch.toLowerCase())),
    [schoolSearch]
  );

  const schoolsNearCommunity = useMemo(() =>
    selectedCommunity ? calcSchoolsNearCommunity(selectedCommunity, maxMinutes) : [],
    [selectedCommunity, maxMinutes]
  );

  const communitiesNearSchool = useMemo(() =>
    selectedSchool ? calcCommunitiesNearSchool(selectedSchool, maxMinutes) : [],
    [selectedSchool, maxMinutes]
  );

  // Fire community_search when school-to-community results are shown
  useEffect(() => {
    if (mode !== 'school-to-community' || !selectedSchool) return;
    const school = SCHOOLS.find(s => s.id === selectedSchool);
    if (!school) return;
    const key = `${selectedSchool}::${maxMinutes}`;
    if (lastCommunitySearchRef.current === key) return;
    lastCommunitySearchRef.current = key;
    trackCommunitySearch({
      school_name: school.name,
      results_count: communitiesNearSchool.length,
    });
  }, [mode, selectedSchool, maxMinutes, communitiesNearSchool.length]);

  const withinThreshold = useMemo(() => {
    if (!selectedCommunity) return [];
    return schoolsNearCommunity.filter(s => s.travelMins <= maxMinutes);
  }, [schoolsNearCommunity, selectedCommunity, maxMinutes]);

  function handleSelectCommunity(name: string) {
    setSelectedCommunity(name);
    setShowCommunityPicker(false);
    setCommunitySearch('');
    // Do NOT call onSelectCommunity here — only update form state
  }

  function handleSelectSchool(id: string) {
    setSelectedSchool(id);
    setShowSchoolPicker(false);
    setSchoolSearch('');
  }

  function handleSubmitCommuteSearch() {
    if (!selectedCommunity) return;
    onSelectCommunity(selectedCommunity, maxMinutes);
    onClose();
  }

  const selectedSchoolName = SCHOOLS.find(s => s.id === selectedSchool)?.name ?? '';

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end" style={{ background: 'rgba(0,0,0,0.45)' }}>
      {/* Backdrop tap to close */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Bottom Sheet */}
      <div
        className="relative bg-white flex flex-col w-full"
        style={{
          borderRadius: '20px 20px 0 0',
          maxHeight: '92vh',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
        }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        {/* Sticky Header */}
        <div className="shrink-0 px-5 pb-3 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0 pr-2">
              <div className="flex items-center gap-2 mb-1">
                <Link
                  href="/school-search-discovery"
                  onClick={onClose}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-800 transition-colors"
                  aria-label="Back to SchoolMapper search"
                >
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
                  SchoolMapper
                </Link>
              </div>
              <h2 className="text-lg font-bold text-[#1e2d5a] leading-tight">Find schools near your community</h2>
              <p className="text-xs text-gray-500 mt-0.5">See which schools are within a realistic morning commute.</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors shrink-0 ml-3 mt-0.5"
              style={{ minWidth: 36, minHeight: 36 }}
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          {/* Segmented control */}
          <div className="mt-3 flex bg-gray-100 rounded-xl p-1 gap-1">
            <button
              onClick={() => setMode('community-to-school')}
              className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${mode === 'community-to-school' ? 'bg-white text-[#1e2d5a] shadow-sm' : 'text-gray-500'}`}
              style={{ minHeight: 36 }}
            >
              🏘 Community → Schools
            </button>
            <button
              onClick={() => setMode('school-to-community')}
              className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${mode === 'school-to-community' ? 'bg-white text-[#1e2d5a] shadow-sm' : 'text-gray-500'}`}
              style={{ minHeight: 36 }}
            >
              🏫 School → Communities
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

          {/* ── Community → Schools mode ── */}
          {mode === 'community-to-school' && (
            <>
              {/* Community selector */}
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2">Where do you live?</p>

                {selectedCommunity && !showCommunityPicker ? (
                  /* Collapsed selected state */
                  <div className="flex items-center justify-between px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-base">📍</span>
                      <span className="text-sm font-semibold text-[#1e2d5a] truncate">{selectedCommunity}</span>
                    </div>
                    <button
                      onClick={() => { setShowCommunityPicker(true); setCommunitySearch(''); setTimeout(() => searchInputRef.current?.focus(), 50); }}
                      className="text-xs font-semibold text-emerald-700 shrink-0 ml-3 underline underline-offset-2"
                      style={{ minHeight: 36 }}
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  /* Search field / picker */
                  <div>
                    <div className="relative">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search community or sub-community"
                        value={communitySearch}
                        onChange={e => setCommunitySearch(e.target.value)}
                        onFocus={() => setShowCommunityPicker(true)}
                        className="w-full pl-9 pr-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400 bg-white"
                        style={{ minHeight: 44 }}
                      />
                    </div>
                    {showCommunityPicker && (
                      <div className="mt-1 border border-gray-200 rounded-xl overflow-hidden shadow-sm max-h-44 overflow-y-auto">
                        {filteredCommunities.length === 0 && (
                          <p className="px-4 py-3 text-sm text-gray-400">No communities found</p>
                        )}
                        {filteredCommunities.map(c => {
                          const cd = ALL_COMMUNITIES_FOR_SEARCH.find(x => x.name === c);
                          return (
                            <button
                              key={c}
                              onClick={() => handleSelectCommunity(c)}
                              className="w-full text-left px-4 py-3 text-sm hover:bg-emerald-50 transition-colors border-b border-gray-100 last:border-0 flex items-center justify-between"
                              style={{ minHeight: 44 }}
                            >
                              <span className="font-medium text-gray-800">{c}</span>
                              {cd?.isSubCommunity && <span className="text-xs text-gray-400 ml-2 shrink-0">sub-community</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Max commute selector */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-semibold text-gray-700">Maximum school commute</p>
                </div>
                <p className="text-xs text-gray-400 mb-2">Based on typical term-time morning traffic.</p>
                <div className="flex gap-2">
                  {COMMUTE_BANDS.map(mins => (
                    <button
                      key={mins}
                      onClick={() => setMaxMinutes(mins)}
                      className={`flex-1 py-2.5 rounded-xl text-xs font-bold border-2 transition-all ${maxMinutes === mins ? 'bg-emerald-500 text-white border-emerald-500 shadow-sm' : 'border-gray-200 text-gray-600 bg-white'}`}
                      style={{ minHeight: 44 }}
                    >
                      {mins}m
                    </button>
                  ))}
                </div>
              </div>

              {/* CTA Button — only control that submits the search */}
              <div className="pt-1">
                <button
                  onClick={handleSubmitCommuteSearch}
                  disabled={!selectedCommunity}
                  className={`w-full py-4 rounded-2xl text-sm font-bold transition-all ${
                    selectedCommunity
                      ? 'bg-emerald-500 text-white shadow-md active:scale-[0.98] hover:bg-emerald-600'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  style={{ minHeight: 52 }}
                >
                  {selectedCommunity
                    ? `Show schools within ${maxMinutes} mins`
                    : 'Select a community first'}
                </button>
              </div>

              {/* Results */}
              {selectedCommunity && (
                <div>
                  {/* Results heading */}
                  <div className="mb-3">
                    <p className="text-base font-bold text-[#1e2d5a] leading-tight">
                      {withinThreshold.length} school{withinThreshold.length !== 1 ? 's' : ''} within {maxMinutes} mins
                    </p>
                    <p className="text-sm text-gray-500">from {selectedCommunity}</p>
                  </div>

                  {/* List / Map toggle */}
                  <div className="flex bg-gray-100 rounded-xl p-1 gap-1 mb-3">
                    <button
                      onClick={() => setResultsView('list')}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all ${resultsView === 'list' ? 'bg-white text-[#1e2d5a] shadow-sm' : 'text-gray-500'}`}
                      style={{ minHeight: 36 }}
                    >
                      <List size={13} /> List
                    </button>
                    <button
                      onClick={() => setResultsView('map')}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all ${resultsView === 'map' ? 'bg-white text-[#1e2d5a] shadow-sm' : 'text-gray-500'}`}
                      style={{ minHeight: 36 }}
                    >
                      <Map size={13} /> Map
                    </button>
                  </div>

                  {resultsView === 'list' ? (
                    <div className="space-y-2">
                      {withinThreshold.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-4">No schools found within {maxMinutes} min. Try increasing the commute time.</p>
                      )}
                      {withinThreshold.map(s => (
                        <Link
                          key={s.id}
                          href={`/school-profile-comparison?school=${s.id}`}
                          onClick={onClose}
                          className="flex items-center justify-between px-4 py-3 bg-white border border-gray-100 rounded-xl hover:border-emerald-200 hover:bg-emerald-50/30 transition-all active:scale-[0.99]"
                          style={{ minHeight: 56 }}
                        >
                          <div className="min-w-0 flex-1 pr-3">
                            <p className="text-sm font-semibold text-[#1e2d5a] leading-tight">{s.name}</p>
                            <p className="text-xs text-gray-500 mt-0.5 truncate">{s.location} · {s.curriculum} · {s.khdaRating}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${s.travelMins <= 10 ? 'bg-emerald-100 text-emerald-700' : s.travelMins <= 20 ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-700'}`}>
                              ~{formatCommuteTime(s.travelMins)} min
                            </span>
                            <ChevronRight size={14} className="text-gray-300" />
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    /* Map placeholder — shows community + school pins */
                    <div className="rounded-2xl overflow-hidden border border-gray-200 bg-gray-50 flex flex-col items-center justify-center" style={{ height: 280 }}>
                      <div className="text-center px-6">
                        <div className="text-4xl mb-3">🗺️</div>
                        <p className="text-sm font-semibold text-[#1e2d5a]">Map view</p>
                        <p className="text-xs text-gray-500 mt-1">Shows {selectedCommunity} and {withinThreshold.length} nearby school{withinThreshold.length !== 1 ? 's' : ''} within {maxMinutes} min</p>
                        <p className="text-xs text-gray-400 mt-2">Interactive map available on the main search page</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── School → Communities mode ── */}
          {mode === 'school-to-community' && (
            <>
              {/* School selector */}
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2">Which school?</p>

                {selectedSchool && !showSchoolPicker ? (
                  <div className="flex items-center justify-between px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-base">🏫</span>
                      <span className="text-sm font-semibold text-[#1e2d5a] truncate">{selectedSchoolName}</span>
                    </div>
                    <button
                      onClick={() => { setShowSchoolPicker(true); setSchoolSearch(''); }}
                      className="text-xs font-semibold text-emerald-700 shrink-0 ml-3 underline underline-offset-2"
                      style={{ minHeight: 36 }}
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="relative">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                      <input
                        type="text"
                        placeholder="Search schools..."
                        value={schoolSearch}
                        onChange={e => setSchoolSearch(e.target.value)}
                        onFocus={() => setShowSchoolPicker(true)}
                        className="w-full pl-9 pr-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400 bg-white"
                        style={{ minHeight: 44 }}
                      />
                    </div>
                    {showSchoolPicker && (
                      <div className="mt-1 border border-gray-200 rounded-xl overflow-hidden shadow-sm max-h-44 overflow-y-auto">
                        {filteredSchools.map(s => (
                          <button
                            key={s.id}
                            onClick={() => handleSelectSchool(s.id)}
                            className="w-full text-left px-4 py-3 text-sm hover:bg-emerald-50 transition-colors border-b border-gray-100 last:border-0"
                            style={{ minHeight: 44 }}
                          >
                            <span className="font-medium text-gray-800">{s.name}</span>
                            <span className="text-xs text-gray-400 ml-2">{s.location}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Max commute selector */}
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1.5">Maximum school commute</p>
                <p className="text-xs text-gray-400 mb-2">Based on typical term-time morning traffic.</p>
                <div className="flex gap-2">
                  {COMMUTE_BANDS.map(mins => (
                    <button
                      key={mins}
                      onClick={() => setMaxMinutes(mins)}
                      className={`flex-1 py-2.5 rounded-xl text-xs font-bold border-2 transition-all ${maxMinutes === mins ? 'bg-emerald-500 text-white border-emerald-500 shadow-sm' : 'border-gray-200 text-gray-600 bg-white'}`}
                      style={{ minHeight: 44 }}
                    >
                      {mins}m
                    </button>
                  ))}
                </div>
              </div>

              {/* Results */}
              {selectedSchool && (
                <div>
                  <div className="mb-3">
                    <p className="text-base font-bold text-[#1e2d5a] leading-tight">
                      {communitiesNearSchool.length} communit{communitiesNearSchool.length !== 1 ? 'ies' : 'y'} within {maxMinutes} mins
                    </p>
                    <p className="text-sm text-gray-500">of {selectedSchoolName}</p>
                  </div>

                  <div className="space-y-2">
                    {communitiesNearSchool.length === 0 && (
                      <p className="text-sm text-gray-400 text-center py-4">No communities found within {maxMinutes} min.</p>
                    )}
                    {communitiesNearSchool.slice(0, 50).map(({ name, mins, isSubCommunity, parentCommunity }) => (
                      <div
                        key={name}
                        className="flex items-center justify-between px-4 py-3 bg-white border border-gray-100 rounded-xl"
                        style={{ minHeight: 52 }}
                      >
                        <div className="min-w-0 flex-1 pr-3">
                          <p className="text-sm font-semibold text-[#1e2d5a] leading-tight">{name}</p>
                          {isSubCommunity && parentCommunity && (
                            <p className="text-xs text-gray-400 mt-0.5">Sub-community of {parentCommunity}</p>
                          )}
                        </div>
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full shrink-0 ${mins <= 10 ? 'bg-emerald-100 text-emerald-700' : mins <= 20 ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-700'}`}>
                          {mins} min
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Bottom padding for safe area */}
          <div className="h-4" />
        </div>
      </div>
    </div>
  );
}

// ─── Desktop Modal (unchanged from original) ─────────────────────────────────

function DesktopCommuteModal({ onClose, onSelectCommunity }: Props) {
  const [mode, setMode] = useState<'school-to-community' | 'community-to-school'>('community-to-school');
  const [selectedCommunity, setSelectedCommunity] = useState('');
  const [selectedSchool, setSelectedSchool] = useState('');
  const [maxMinutes, setMaxMinutes] = useState<number>(20);
  const [communitySearch, setCommunitySearch] = useState('');
  const [schoolSearch, setSchoolSearch] = useState('');

  const filteredCommunities = useMemo(() =>
    ALL_COMMUNITY_NAMES.filter(c => c.toLowerCase().includes(communitySearch.toLowerCase())).slice(0, 80),
    [communitySearch]
  );

  const filteredSchools = useMemo(() =>
    SCHOOLS.filter(s => s.name.toLowerCase().includes(schoolSearch.toLowerCase())).slice(0, 30),
    [schoolSearch]
  );

  const schoolsNearCommunity = useMemo(() =>
    selectedCommunity ? calcSchoolsNearCommunity(selectedCommunity, maxMinutes) : [],
    [selectedCommunity, maxMinutes]
  );

  const communitiesNearSchool = useMemo(() =>
    selectedSchool ? calcCommunitiesNearSchool(selectedSchool, maxMinutes) : [],
    [selectedSchool, maxMinutes]
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="font-display font-700 text-lg text-foreground">🚗 Commute Time Search</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Find schools or communities within peak term-time travel (includes sub-communities)</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Mode toggle */}
        <div className="flex p-4 gap-2 border-b border-border">
          <button
            onClick={() => setMode('community-to-school')}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${mode === 'community-to-school' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
          >
            🏘 Community → Schools
          </button>
          <button
            onClick={() => setMode('school-to-community')}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${mode === 'school-to-community' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
          >
            🏫 School → Communities
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Max minutes selector */}
          <div>
            <label className="text-sm font-semibold text-foreground mb-2 block">
              <Clock size={14} className="inline mr-1" />
              Max peak commute time
            </label>
            <div className="flex gap-2 flex-wrap">
              {COMMUTE_BANDS.map(mins => (
                <button
                  key={mins}
                  onClick={() => setMaxMinutes(mins)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${maxMinutes === mins ? 'bg-primary text-white border-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}
                >
                  {mins} min
                </button>
              ))}
            </div>
          </div>

          {mode === 'community-to-school' ? (
            <>
              <div>
                <label className="text-sm font-semibold text-foreground mb-2 block">
                  <MapPin size={14} className="inline mr-1" />
                  Select your community (including sub-communities)
                </label>
                <div className="relative mb-2">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search communities and sub-communities..."
                    value={communitySearch}
                    onChange={e => setCommunitySearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div className="max-h-40 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                  {filteredCommunities.map(c => {
                    const communityData = ALL_COMMUNITIES_FOR_SEARCH.find(x => x.name === c);
                    return (
                      <button
                        key={c}
                        onClick={() => setSelectedCommunity(c)}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${selectedCommunity === c ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted text-foreground'}`}
                      >
                        <span>{c}</span>
                        {communityData?.isSubCommunity && (
                          <span className="ml-2 text-xs text-muted-foreground">(sub-community)</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {selectedCommunity && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-foreground">
                      {schoolsNearCommunity.length} schools within {maxMinutes} min of {selectedCommunity}
                    </p>
                    <button
                      onClick={() => onSelectCommunity(selectedCommunity, maxMinutes)}
                      className="px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      Apply Filter
                    </button>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {schoolsNearCommunity.map(s => (
                      <div key={s.id} className="flex items-center justify-between px-3 py-2 bg-muted/50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-foreground">{s.name}</p>
                          <p className="text-xs text-muted-foreground">{s.location} · {s.curriculum} · {s.khdaRating}</p>
                        </div>
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${s.travelMins <= 10 ? 'bg-emerald-100 text-emerald-700' : s.travelMins <= 20 ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                          ~{formatCommuteTime(s.travelMins)} min
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <label className="text-sm font-semibold text-foreground mb-2 block">
                  <Search size={14} className="inline mr-1" />
                  Select a school
                </label>
                <div className="relative mb-2">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search schools..."
                    value={schoolSearch}
                    onChange={e => setSchoolSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div className="max-h-40 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                  {filteredSchools.map(s => (
                    <button
                      key={s.id}
                      onClick={() => setSelectedSchool(s.id)}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${selectedSchool === s.id ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted text-foreground'}`}
                    >
                      <span className="font-medium">{s.name}</span>
                      <span className="text-muted-foreground ml-2 text-xs">{s.location}</span>
                    </button>
                  ))}
                </div>
              </div>

              {selectedSchool && (
                <div>
                  <p className="text-sm font-semibold text-foreground mb-2">
                    {communitiesNearSchool.length} communities within {maxMinutes} min of {SCHOOLS.find(s => s.id === selectedSchool)?.shortName}
                    <span className="text-xs text-muted-foreground ml-1">(incl. sub-communities)</span>
                  </p>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {communitiesNearSchool.slice(0, 50).map(({ name, mins, isSubCommunity, parentCommunity }) => (
                      <div key={name} className="flex items-center justify-between px-3 py-2 bg-muted/50 rounded-lg">
                        <div>
                          <p className="text-sm text-foreground">{name}</p>
                          {isSubCommunity && parentCommunity && (
                            <p className="text-xs text-muted-foreground">Sub-community of {parentCommunity}</p>
                          )}
                        </div>
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${mins <= 10 ? 'bg-emerald-100 text-emerald-700' : mins <= 20 ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                          ~{mins} min
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Responsive wrapper ───────────────────────────────────────────────────────

export default function CommuteSearchPanel({ onClose, onSelectCommunity }: Props) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  if (isMobile) {
    return <MobileCommuteSheet onClose={onClose} onSelectCommunity={onSelectCommunity} />;
  }
  return <DesktopCommuteModal onClose={onClose} onSelectCommunity={onSelectCommunity} />;
}
